import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Positive, default-OFF organisation opt-in for the Care Today client
 * experience. Global rows never enable it; OFF keeps the existing client page.
 */
export const CARE_TODAY_V1_FLAG = 'care_today_v1';

/** Shown where the flag is on but its database changes are not yet applied. */
export const CARE_TODAY_SCHEMA_NOT_READY_MESSAGE =
  'Care Today stays off until its database changes are applied. Ask Statura support to confirm the Care Today migrations are live before switching it on.';

const loggedSchemaNotReady = new Set<string>();

/** Logs once per tenant with identifiers only. */
function logSchemaNotReady(tenantId: string) {
  if (loggedSchemaNotReady.has(tenantId)) return;
  loggedSchemaNotReady.add(tenantId);
  console.warn('care_today_v1 held off: care_today_schema_ready is not true', { tenantId });
}

/**
 * True only when the flag-ON database objects exist (placeholder migrations
 * 20435000002766-2770). A missing function or failed check is not ready.
 */
export async function loadCareTodaySchemaReady(sb: SupabaseClient): Promise<boolean> {
  try {
    const { data, error } = await sb.rpc('care_today_schema_ready');
    return !error && data === true;
  } catch {
    return false;
  }
}

/**
 * Same exact-tenant feature_flags pattern as qi_improvements_v1; no env or new
 * schema. Fails closed: an ON row counts as OFF until the schema is ready.
 */
export async function loadCareTodayEnabled(
  sb: SupabaseClient,
  tenantId?: string | null,
): Promise<boolean> {
  if (!tenantId?.trim()) return false;
  const tenant = tenantId.trim().toLowerCase();
  try {
    const { data, error } = await sb
      .from('feature_flags')
      .select('enabled')
      .eq('tenant_id', tenant)
      .eq('flag_key', CARE_TODAY_V1_FLAG)
      .maybeSingle();
    if (error || data?.enabled !== true) return false;
  } catch {
    return false;
  }
  if (await loadCareTodaySchemaReady(sb)) return true;
  logSchemaNotReady(tenant);
  return false;
}
